import { useCallback } from 'react';
import { SalesOrderResponseDto } from '../../../../api';
import { formatCurrency as formatCurrencyValue } from '../../../../utils/formatters/money';
import {
  canExchangeSalesOrder,
  canProcessSalesOrderPayment,
} from '../../../../utils/sales/sales-order-actions';

export interface UseOrdersTableProps {
  orders: SalesOrderResponseDto[];
  onViewOrder: (id: number) => void;
  onEditOrder: (id: number) => void;
  onDeliverOrder: (id: number) => void;
  onPrintReceipt?: (id: number) => void;
  onProcessPayment: (id: number) => void;
  onReturnOrder: (id: number, orderCode?: string) => void;
  onExchangeOrder?: (id: number) => void;
}

export const useOrdersTable = ({
  orders,
  onViewOrder,
  onEditOrder,
  onDeliverOrder,
  onPrintReceipt,
  onProcessPayment,
  onReturnOrder,
  onExchangeOrder,
}: UseOrdersTableProps) => {
  // Configuración de status
  const getStatusConfig = useCallback((status: string) => {
    const statusConfig = {
      DRAFT: { color: 'default' as const, label: 'Borrador' },
      CONFIRMED: { color: 'default' as const, label: 'Confirmada' },
      PARTIAL_PAID: { color: 'warning' as const, label: 'Pago Parcial' },
      PAID: { color: 'success' as const, label: 'Pagada' },
      DELIVERED: { color: 'success' as const, label: 'Entregada' },
      CANCELLED: { color: 'error' as const, label: 'Cancelada' },
      REFUNDED: { color: 'error' as const, label: 'Reembolsada' },
    };

    return statusConfig[status as keyof typeof statusConfig] || statusConfig['DRAFT'];
  }, []);

  // Formateo de fecha
  const formatDate = useCallback((date: Date | string) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Formateo de moneda
  const formatCurrency = useCallback((amount: number) => {
    return formatCurrencyValue(amount);
  }, []);

  const canProcessPayment = useCallback(
    (status: string) => canProcessSalesOrderPayment(status),
    [],
  );

  // Determinar si se puede devolver
  const canReturn = useCallback((status: string) => {
    return !['REFUNDED'].includes(status);
  }, []);

  // Intercambio: status financiero OK y envío mixta cerrado (o venta local)
  const canExchange = useCallback(
    (
      _orderId: number,
      status: string,
      fulfillmentStatus?: string | null,
      deliveryStatus?: string | null,
    ) => canExchangeSalesOrder(status, fulfillmentStatus, deliveryStatus),
    [],
  );

  // Handlers
  const handleViewOrder = useCallback(
    (id: number) => {
      onViewOrder(id);
    },
    [onViewOrder],
  );

  const handleEditOrder = useCallback(
    (id: number) => {
      onEditOrder(id);
    },
    [onEditOrder],
  );

  const handleDeliverOrder = useCallback(
    (id: number) => {
      onDeliverOrder(id);
    },
    [onDeliverOrder],
  );

  const handlePrintReceipt = useCallback(
    (id: number) => {
      onPrintReceipt?.(id);
    },
    [onPrintReceipt],
  );

  const handleProcessPayment = useCallback(
    (id: number) => {
      onProcessPayment(id);
    },
    [onProcessPayment],
  );

  const handleReturnOrder = useCallback(
    (id: number, orderCode?: string) => {
      onReturnOrder(id, orderCode);
    },
    [onReturnOrder],
  );

  const handleExchangeOrder = useCallback(
    (id: number) => {
      if (onExchangeOrder) {
        onExchangeOrder(id);
      }
    },
    [onExchangeOrder],
  );

  // Flags de estado
  const hasOrders = orders.length > 0;

  return {
    getStatusConfig,
    formatDate,
    formatCurrency,
    canProcessPayment,
    canReturn,
    canExchange,
    hasOrders,
    handleViewOrder,
    handleEditOrder,
    handleDeliverOrder,
    handlePrintReceipt,
    handleProcessPayment,
    handleReturnOrder,
    handleExchangeOrder,
  };
};
